import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import emailjs from "@emailjs/browser"; 
import { FaEnvelope, FaCheck, FaUser, FaCreditCard, FaShoppingBag } from "react-icons/fa";
import { FiPhone } from "react-icons/fi";
import { useCart } from "./CartContext";
import { useAuth } from "../auth/useAuth";

import "./PaymentPage.css";

// ✅ IMPORT MANUAL ID MAP (Make sure SampleProducts.js is in src/components/)
import { getProductId } from "./SampleProducts";

function PaymentPage({setToggle,onClose}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { clearCart } = useCart();
  const { user } = useAuth(); 
  const summary = useMemo(() => location.state?.summary || {}, [location.state?.summary]);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "https://backend-ewrn.onrender.com";

  const cartItems = useMemo(() => location.state?.cartItems || [], [location.state?.cartItems]);
  
  // ✅ LOADING OVERLAY STATE
  const [isConfirming, setIsConfirming] = useState(false);
  const [loadingText, setLoadingText] = useState("Confirming Payment...");

  // Form States
  const [userName, setUserName] = useState("");
  const [userEmail, setUserEmail] = useState("");
  const [userContact, setUserContact] = useState("");
  const [altContact, setAltContact] = useState("");

  // Validation States
  const [invalidName, setInvalidName] = useState(false);
  const [invalidEmail, setInvalidEmail] = useState(false);
  const [invalidContact, setInvalidContact] = useState(false);
  const [invalidAltContact, setInvalidAltContact] = useState(false);
  const [shakeName, setShakeName] = useState(false);
  const [shakeEmail, setShakeEmail] = useState(false);
  const [shakeContact, setShakeContact] = useState(false);
  const [shakeAltContact, setShakeAltContact] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const totalAmount = useMemo(
    () => cartItems.reduce((sum, item) => sum + item.quantity * item.price, 0).toFixed(2),
    [cartItems]
  );

  useEffect(() => {
    if (cartItems.length === 0) navigate("/checkout");
  }, [cartItems, navigate]);

  // ✅ HELPER: Get ID from Manual File
  const getProductIdFromName = (name) => {
    return getProductId(name); 
  };

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const contactRegex = /^\d{10}$/;
  const isNameValid = () => userName.trim().length > 0;
  const isEmailValid = () => emailRegex.test(userEmail);
  const isContactValid = () => contactRegex.test(userContact);
  const isAltContactValid = () => altContact === "" || contactRegex.test(altContact);
  const isFormValid = () => isNameValid() && isEmailValid() && isContactValid() && isAltContactValid();

  // ✅ EMAIL FUNCTION (ASYNC + PRODUCT ID FIX)
  const sendEmail = async (razorpay_order_id, razorpay_payment_id) => {
    
    // 1. Capture Main Product ID
    const firstProduct = cartItems[0] || {};
    const mainProductId = getProductIdFromName(firstProduct.name); 

    // ✅ UPDATE THIS PART IN sendEmail FUNCTION
  const itemsHtml = cartItems.map(item => {
    const uniqueProductId = getProductIdFromName(item.name);
    return `
    <tr>
      <td style="padding: 12px 0; border-bottom: 1px solid #eee; width: 60%; vertical-align: top;">
        <strong style="font-size: 14px; color: #333; display: block; margin-bottom: 4px;">${item.name}</strong>
        <span style="font-size: 12px; color: #777;">Qty: ${item.quantity}</span>
      </td>

      <td style="padding: 12px 0; border-bottom: 1px solid #eee; width: 40%; text-align: right; vertical-align: top;">
        <div style="margin-bottom: 4px;">
          <span style="background: #f3f4f6; border: 1px solid #e5e7eb; color: #555; padding: 2px 6px; border-radius: 4px; font-size: 10px; font-family: monospace;">
            ID: ${uniqueProductId}
          </span>
        </div>
        <strong style="font-size: 14px; color: #333;">₹${item.price}</strong>
      </td>
    </tr>
    `;
  }).join('');

    const templateParams = {
      name: userName,
      user_id: user?.user_id || "GUEST_USER",
      email: userEmail,
      order_id: razorpay_order_id,
      transaction_id: razorpay_payment_id,
      total_amount: totalAmount,
      message_html: itemsHtml,
      
      // ✅ FIX: Adding product_id here so it shows in email
      product_id: mainProductId, 
    };

    try {
      await emailjs.send("service_5u1kekh", "template_5hmbcps", templateParams, "u9Jrq52Xpl01Cq3Kh");
    } catch (err) {
      console.error("Email send failed:", err);
    }
  };

  const loadRazorpayScript = (src) => new Promise((resolve) => {
    const script = document.createElement("script");
    script.src = src;
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });

  const handlePayClick = async () => {
      setToggle(false)
       onClose()
    setErrorMsg("");
    if (!isFormValid()) {
      setErrorMsg("*Please fill in all required fields");
      if (!isNameValid()) { setInvalidName(true); setShakeName(true); setTimeout(() => setShakeName(false), 500); }
      if (!isEmailValid()) { setInvalidEmail(true); setShakeEmail(true); setTimeout(() => setShakeEmail(false), 500); }
      if (!isContactValid()) { setInvalidContact(true); setShakeContact(true); setTimeout(() => setShakeContact(false), 500); }
      if (!isAltContactValid()) { setInvalidAltContact(true); setShakeAltContact(true); setTimeout(() => setShakeAltContact(false), 500); }
      return;
    }
    await initiatePayment();
  };

  const initiatePayment = async () => {
    if (parseFloat(totalAmount) < 1) { alert("Cart total must be at least ₹1"); return; }
    
    const res = await loadRazorpayScript("https://checkout.razorpay.com/v1/checkout.js");
    if (!res) { alert("Razorpay SDK failed to load."); return; }

    try {
      const firstProduct = cartItems[0] || {};
      const userId = user?.user_id;
      const fixedProductId = getProductIdFromName(firstProduct.name);

      const orderResponse = await fetch(`${BACKEND_URL}/payment/create-order`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
            amount: totalAmount,
            user_id: userId,
            product_id: fixedProductId
        }),
      });

      const { order, key_id } = await orderResponse.json();

      if (!order) throw new Error("Order creation failed");
      
      //const totalQty = cartItems.reduce((sum, item) => sum + Number(item.quantity), 0);

      const options = {
        key: key_id || "rzp_test_RKuD49k1DsaIa2",
        amount: order.amount,
        currency: order.currency,
        name: "Marsmate | Store",
        description: "E-commerce Checkout",
        image:"https://i.postimg.cc/mr6Mdf1P/Payment-logo-(1).png", 
        order_id: order.id,
        
        handler: async function (response) {
          try {
            // ✅ START OVERLAY
            setIsConfirming(true);
            setLoadingText("Verifying Payment...");

            // 1. Verify
            await fetch(`${BACKEND_URL}/payment/verify-payment`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              credentials: "include",
              body: JSON.stringify({
                user_id: userId,
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
                amount: totalAmount,
                currency: "INR"
              }),
            });

            // 2. Store
            // Inside handler: async function (response)
            setLoadingText("Securing Order Details...");

            // ✅ 1. Create the granular items array
            const detailedItems = cartItems.map(item => ({
                product_id: getProductIdFromName(item.name),
                name: item.name,
                original_price: item.originalPrice, // Individual Item Original
                discounted_price: item.price,       // Individual Item Paid
                quantity: item.quantity,
                total: (item.price * item.quantity).toFixed(2)
            }));

            // ✅ 2. Send the full breakdown to the backend
            await fetch(`${BACKEND_URL}/order/store-order`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    user_id: userId,
                    payment_id: response.razorpay_payment_id,
                    razorpay_order_id: response.razorpay_order_id,
                    contact: userContact,
                    name: userName,
                    email: userEmail,
                    amount: Math.round(totalAmount),
                    product_name: cartItems.map((item) => item.name).join(", "),
                    
                    // NEW FIELDS TO STORE
                    items: detailedItems, // All individual prices are here
                    total_original: summary.totalOriginalPrice,
                    total_savings: summary.totalSavings,
                    quantity: cartItems.reduce((sum, item) => sum + Number(item.quantity), 0),
                }),
            });
           
            // 3. Email
            setLoadingText("Sending Confirmation Email...");
            await sendEmail(response.razorpay_order_id, response.razorpay_payment_id);
            
            setLoadingText("Success!");
            
           setTimeout(() => {
  

  clearCart();
  navigate(`/orders`);
}, 1000);


          } catch (err) {
            console.error(err);
            setIsConfirming(false);
            alert("Payment successful but network error occurred.");
             navigate(`/orders`);
          }
        },
        prefill: { email: userEmail || "", contact: userContact || "" },
        theme: { color: "#00204a" },
        modal: { ondismiss: () => setIsConfirming(false) },
      };

      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", () => alert("Payment Failed"));
      rzp.open();
    } catch (error) {
      console.error(error);
    }
  };

  const renderInput = (type, value, setValue, placeholder, invalid, tooltip, shake, isOptional = false) => {
    let Icon = type === "email" ? FaEnvelope : type === "tel" ? FiPhone : FaUser;
    const handleChange = (e) => {
      if (type === "tel") e.target.value = e.target.value.replace(/\D/g, "");
      setValue(e.target.value);
      if (type === "email") setInvalidEmail(false);
      if (type === "tel") tooltip === "Invalid Contact Number" ? setInvalidContact(false) : setInvalidAltContact(false);
      if (type === "text") setInvalidName(false);
    };
    const isValid = type === "email" ? emailRegex.test(value)
      : type === "tel" ? (tooltip === "Invalid Contact Number" ? contactRegex.test(value) : value === "" || contactRegex.test(value))
      : value.trim().length > 0;
    const showValidation = value !== ""; 

    return (
      <div className="input-group">
        <div className={`input-container ${shake ? 'shake' : ''} ${invalid ? 'invalid' : ''}`}>
          <Icon className="input-icon" />
          <input type={type} placeholder={placeholder} value={value} onChange={handleChange} className="input-field" />
          {showValidation && isValid && (<FaCheck className="input-checkmark" />)}
        </div>
        {(invalid || (showValidation && !isValid && !isOptional)) && (<div className="input-error">{tooltip}</div>)}
      </div>
    );
  };

  // ✅ INLINE STYLES FOR OVERLAY
  const styles = {
    overlay: {
      position: "fixed",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      backgroundColor: "rgba(255, 255, 255, 0.95)", 
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "center",
      zIndex: 9999, 
    },
    spinner: {
      width: "50px",
      height: "50px",
      border: "5px solid #f3f3f3",
      borderTop: "5px solid #00204a", 
      borderRadius: "50%",
      marginBottom: "20px",
      animation: "spin 1s linear infinite",
    },
    text: {
      fontSize: "20px",
      fontWeight: "600",
      color: "#333",
      fontFamily: "'Segoe UI', sans-serif",
    }
  };

  return (
    <div className="payment-page">
      {/* ✅ LOADING OVERLAY */}
      {isConfirming && (
        <div style={styles.overlay}>
            <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
            <div style={styles.spinner}></div>
            <div style={styles.text}>{loadingText}</div>
        </div>
      )}

      <div className="payment-container">
        <div className="payment-header">
          <h1 className="payment-title"><FaCreditCard /> Secure Payment</h1>
          <p className="payment-subtitle">Complete your purchase securely</p>
        </div>

        <div className="payment-form-container">
          <div className="payment-summary" style={{ marginBottom: "25px", borderBottom: "1px solid #eee", paddingBottom: "15px" }}>
            <div className="summary-title" style={{ marginBottom: "15px" }}><FaShoppingBag /> Order Summary</div>
            <div className="summary-item"><span className="summary-label">Items in Cart:</span><span className="summary-value">{cartItems.length}</span></div>
            <div className="summary-item"><span className="summary-label">Total Amount:</span><span className="summary-value">₹{totalAmount}</span></div>
          </div>

          <h3 className="payment-form-title"><FaUser /> Customer Information</h3>
          {renderInput("text", userName, setUserName, "Enter Full Name", invalidName, "Name is required", shakeName)}
          {renderInput("email", userEmail, setUserEmail, "Enter Email Address", invalidEmail, "Enter a valid email", shakeEmail)}
          {renderInput("tel", userContact, setUserContact, "Enter Contact Number", invalidContact, "Invalid contact number", shakeContact)}
          {renderInput("tel", altContact, setAltContact, "Enter Alternative Contact (Optional)", invalidAltContact, "Invalid alternative contact", shakeAltContact, true)}
          
          {errorMsg && <div className="form-error">{errorMsg}</div>}
          
          <div className="payment-button-container">
            <button onClick={handlePayClick} className="payment-button" disabled={!isFormValid() || isConfirming}>
              <span>Pay Now</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PaymentPage;
